import '@mcp-b/global';
import React, { useContext, useEffect } from 'react';
import { useWebMCP, type InferOutput } from '@mcp-b/react-webmcp';
import { EditorStoreContext } from 'adflow';
import { MCP_B_RELAY_EMBED_SCRIPT_ID, MCP_B_RELAY_EMBED_SRC } from './constants';
import { useTemplateWebMCPFeature } from './hooks/useTemplateWebMCPFeature';
import { logTemplateWebMCP } from './logging';
import {
  executeTemplateBuilderApply,
  TEMPLATE_BUILDER_APPLY_TOOL,
} from './tools/templateBuilderApply';
import {
  executeTemplateBuilderCapability,
  TEMPLATE_BUILDER_CAPABILITY_TOOL,
} from './tools/templateBuilderCapability';
import {
  executeTemplateBuilderRead,
  TEMPLATE_BUILDER_READ_TOOL,
} from './tools/templateBuilderRead';
import {
  executeTemplateBuilderSearchReference,
  TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL,
} from './tools/templateBuilderSearchReference';
import type { TemplateRouteMode } from './toolContext';

const RELAY_WIDGET_SELECTOR = '[data-webmcp-relay]';

function isRelayRejection(
  data: unknown,
): data is { type: 'webmcp.relay.rejected'; reason?: string; message?: string } {
  return (
    Boolean(data) &&
    typeof data === 'object' &&
    (data as { type?: unknown }).type === 'webmcp.relay.rejected'
  );
}

type Props = {
  templateId: string;
  editorReady: boolean;
  hasEditPermissions: boolean;
  routeMode?: TemplateRouteMode;
  listAdvertisers?: () => Promise<Array<{ _id?: string | number; name?: string; type?: string }>>;
  loadAdvertiserFonts?: (
    businessEntityId: number,
  ) => Promise<Array<{ fontFamily: string; src?: string }>>;
};

type ReadToolOutput = InferOutput<typeof TEMPLATE_BUILDER_READ_TOOL.outputSchema>;

function useGatedRelayEmbed(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let script = document.getElementById(MCP_B_RELAY_EMBED_SCRIPT_ID) as HTMLScriptElement | null;
    const created = !script;
    if (!script) {
      script = document.createElement('script');
      script.id = MCP_B_RELAY_EMBED_SCRIPT_ID;
      script.src = MCP_B_RELAY_EMBED_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    // The vendored widget treats a rejected hello as a dropped socket and
    // reconnects about every 500ms. host-origin-not-allowed will not change
    // until the relay process is restarted, so remove the widget after one rejection.
    const onMessage = (event: MessageEvent) => {
      if (!isRelayRejection(event.data)) {
        return;
      }
      const iframe = document.querySelector(RELAY_WIDGET_SELECTOR);
      if (!(iframe instanceof HTMLIFrameElement) || event.source !== iframe.contentWindow) {
        return;
      }
      iframe.remove();
      logTemplateWebMCP('relay_rejected', {
        reason: event.data.reason,
        message: event.data.message,
      });
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      if (created) {
        script?.remove();
      }
    };
  }, [enabled]);
}

function TemplateWebMCPTools({
  templateId,
  hasEditPermissions,
  routeMode,
  listAdvertisers,
  loadAdvertiserFonts,
}: {
  templateId: string;
  hasEditPermissions: boolean;
  routeMode: TemplateRouteMode;
  listAdvertisers?: () => Promise<Array<{ _id?: string | number; name?: string; type?: string }>>;
  loadAdvertiserFonts?: (
    businessEntityId: number,
  ) => Promise<Array<{ fontFamily: string; src?: string }>>;
}) {
  const editorContext = useContext(EditorStoreContext);
  const tools = [
    TEMPLATE_BUILDER_READ_TOOL,
    TEMPLATE_BUILDER_APPLY_TOOL,
    TEMPLATE_BUILDER_CAPABILITY_TOOL,
    TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL,
  ];

  useEffect(() => {
    for (const tool of tools) {
      logTemplateWebMCP('register', { tool: tool.name });
    }
    return () => {
      for (const tool of tools) {
        logTemplateWebMCP('unregister', { tool: tool.name });
      }
    };
  }, []);

  const context = {
    store: editorContext.store as {
      getState: () => Record<string, unknown>;
    } | null,
    templateId,
    canEdit: hasEditPermissions,
    routeMode,
    listAdvertisers,
    loadAdvertiserFonts,
  };

  // Every `useWebMCP` field, with what actually reaches a relay listing today:
  // - Relayed: name, description, inputSchema, annotations
  // - Registered in-page only until the embed prefers `listTools()`: outputSchema
  // - Hook-local (never listed): enabled, execute
  useWebMCP({
    name: TEMPLATE_BUILDER_READ_TOOL.name,
    description: TEMPLATE_BUILDER_READ_TOOL.description,
    enabled: TEMPLATE_BUILDER_READ_TOOL.enabled,
    inputSchema: TEMPLATE_BUILDER_READ_TOOL.inputSchema,
    outputSchema: TEMPLATE_BUILDER_READ_TOOL.outputSchema,
    annotations: TEMPLATE_BUILDER_READ_TOOL.annotations,
    // `execute` is typed as returning the bare payload, which the polyfill would then wrap
    // itself. We return a full tool result instead, because `normalizeToolResponse` passes
    // through any object carrying a `content` array and that is the only way to keep our
    // human-readable first content block and `isError`. The cast records that gap.
    execute: (input: unknown) =>
      executeTemplateBuilderRead({ ...context, input }) as unknown as Promise<ReadToolOutput>,
  });
  useWebMCP({
    name: TEMPLATE_BUILDER_APPLY_TOOL.name,
    description: TEMPLATE_BUILDER_APPLY_TOOL.description,
    enabled: TEMPLATE_BUILDER_APPLY_TOOL.enabled,
    inputSchema: TEMPLATE_BUILDER_APPLY_TOOL.inputSchema,
    outputSchema: TEMPLATE_BUILDER_APPLY_TOOL.outputSchema,
    annotations: TEMPLATE_BUILDER_APPLY_TOOL.annotations,
    execute: (input: unknown) =>
      executeTemplateBuilderApply({ ...context, input }) as unknown as Promise<ReadToolOutput>,
  });
  useWebMCP({
    name: TEMPLATE_BUILDER_CAPABILITY_TOOL.name,
    description: TEMPLATE_BUILDER_CAPABILITY_TOOL.description,
    enabled: TEMPLATE_BUILDER_CAPABILITY_TOOL.enabled,
    inputSchema: TEMPLATE_BUILDER_CAPABILITY_TOOL.inputSchema,
    outputSchema: TEMPLATE_BUILDER_CAPABILITY_TOOL.outputSchema,
    annotations: TEMPLATE_BUILDER_CAPABILITY_TOOL.annotations,
    execute: (input: unknown) =>
      executeTemplateBuilderCapability({ ...context, input }) as unknown as Promise<ReadToolOutput>,
  });
  useWebMCP({
    name: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.name,
    description: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.description,
    enabled: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.enabled,
    inputSchema: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.inputSchema,
    outputSchema: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.outputSchema,
    annotations: TEMPLATE_BUILDER_SEARCH_REFERENCE_TOOL.annotations,
    execute: (input: unknown) =>
      executeTemplateBuilderSearchReference({
        ...context,
        input,
      }) as unknown as Promise<ReadToolOutput>,
  });

  return null;
}

function TemplateWebMCPHost({
  templateId,
  editorReady,
  hasEditPermissions,
  routeMode = 'edit',
  listAdvertisers,
  loadAdvertiserFonts,
}: Props) {
  const featureEnabled = useTemplateWebMCPFeature();
  const shouldRegister = featureEnabled && editorReady && hasEditPermissions;
  useGatedRelayEmbed(shouldRegister);

  if (!shouldRegister) {
    return null;
  }

  return (
    <TemplateWebMCPTools
      templateId={templateId}
      hasEditPermissions={hasEditPermissions}
      routeMode={routeMode}
      listAdvertisers={listAdvertisers}
      loadAdvertiserFonts={loadAdvertiserFonts}
    />
  );
}

export default TemplateWebMCPHost;
